import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Brain, Clock, Mail, MessageSquare, FileText, StickyNote, Activity, CheckCircle2, XCircle, Loader2, RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import agentCasesService, { type AgentCaseListItem, type AgentCaseEvent } from '@/services/agent-cases.service';
import { formatDayTime } from '@/lib/format';

// Beschriftungen bleiben als deutscher Quelltext im Code (defaultValue) und
// werden erst beim Rendern übersetzt — auf Modulebene darf t() nicht laufen.
const STATUS_META: Record<string, { key: string; label: string; cls: string }> = {
  active: { key: 'agent:caseStatus.active', label: 'Aktiv', cls: 'bg-blue-50 text-blue-600' },
  waiting_reply: { key: 'agent:caseStatus.waiting_reply', label: 'Wartet auf Antwort', cls: 'bg-amber-50 text-amber-600' },
  waiting_time: { key: 'agent:caseStatus.waiting_time', label: 'Wiedervorlage', cls: 'bg-violet-50 text-violet-600' },
  ready: { key: 'agent:caseStatus.ready', label: 'Bereit', cls: 'bg-emerald-50 text-emerald-600' },
  done: { key: 'agent:caseStatus.done', label: 'Abgeschlossen', cls: 'bg-slate-100 text-slate-500' },
  failed: { key: 'agent:caseStatus.failed', label: 'Fehlgeschlagen', cls: 'bg-rose-50 text-rose-600' },
};

const EVENT_META: Record<string, { key: string; label: string; icon: React.ElementType }> = {
  status: { key: 'agent:event.status', label: 'Status', icon: Activity },
  thought: { key: 'agent:event.thought', label: 'Gedanke', icon: Brain },
  action: { key: 'agent:event.action', label: 'Aktion', icon: Clock },
  internal_comm: { key: 'agent:event.internal_comm', label: 'Interne Kommunikation', icon: Mail },
  reply_received: { key: 'agent:event.reply_received', label: 'Antwort erhalten', icon: MessageSquare },
  draft: { key: 'agent:event.draft', label: 'Kundenentwurf', icon: FileText },
  note: { key: 'agent:event.note', label: 'Notiz', icon: StickyNote },
};

const fmt = (d?: string) => {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime()) ? '' : formatDayTime(t);
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const m = STATUS_META[status];
  const label = m ? t(m.key, m.label) : status;
  const cls = m?.cls || 'bg-slate-100 text-slate-500';
  return (
    <span className={cn('agent-case-status', `agent-case-status--${status}`, cls)}>
      <span aria-hidden="true" className="agent-case-status-dot" />
      {label}
    </span>
  );
};

const TimelineItem: React.FC<{ ev: AgentCaseEvent }> = ({ ev }) => {
  const { t } = useTranslation();
  const m = EVENT_META[ev.kind];
  const label = m ? t(m.key, m.label) : ev.kind;
  const Icon = m?.icon || Activity;
  const meta = ev.metadata as Record<string, any> | null;
  return (
    <article className={cn('cloud-agent-event', `cloud-agent-event--${ev.kind}`)}>
      <div className="cloud-agent-event-icon" aria-hidden="true">
        <Icon />
      </div>
      <div className="cloud-agent-event-content">
        <div className="cloud-agent-event-heading">
          <span className="cloud-agent-event-label">{label}</span>
          <span className="cloud-agent-event-time">{fmt(ev.created_at)}</span>
          {meta?.child_ticket_number && (
            <span className="cloud-agent-event-reference">{String(meta.child_ticket_number)}</span>
          )}
        </div>
        {ev.content && <p className="cloud-agent-event-copy">{ev.content}</p>}
      </div>
    </article>
  );
};

export const CaseDetail: React.FC<{ ticketId: string }> = ({ ticketId }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['agent-case', ticketId],
    queryFn: () => agentCasesService.getByTicket(ticketId),
    refetchInterval: 15000,
  });

  if (isLoading) return <div className="cloud-agent-state"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!data) return <div className="cloud-agent-state">{t('common:x3.noCaseFound', 'Kein Fall gefunden')}</div>;

  const c = data.case;
  return (
    <div className="cloud-agent-detail">
      <header className="cloud-agent-overview">
        <div className="cloud-agent-overview-meta">
          <span className="cloud-agent-ticket-number">{c.ticket_number}</span>
          <StatusBadge status={c.status} />
        </div>
        <h3 className="cloud-agent-title">{c.subject}</h3>
        {c.summary && <p className="cloud-agent-summary">{c.summary}</p>}
      </header>
      <section className="cloud-agent-activity">
        <div className="cloud-agent-section-heading">
          <span aria-hidden="true" className="cloud-agent-section-accent" />
          <span>{t('agent:drafts.conversation', 'Verlauf')}</span>
          <span className="cloud-agent-event-count">{data.events.length}</span>
          <span aria-hidden="true" className="cloud-agent-section-rule" />
        </div>
        {data.events.length === 0 ? (
          <div className="cloud-agent-empty">{t('agent:drafts.noEvents', 'Noch keine Ereignisse')}</div>
        ) : (
          <div className="cloud-agent-event-list">{data.events.map((ev, i) => <TimelineItem key={i} ev={ev} />)}</div>
        )}
      </section>
    </div>
  );
};

const AgentCasesView: React.FC = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const { data: cases = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['agent-cases'],
    queryFn: () => agentCasesService.list(),
    refetchInterval: 15000,
  });

  const active = cases.filter((c) => !['done', 'failed'].includes(c.status));
  const doneList = cases.filter((c) => ['done', 'failed'].includes(c.status));
  const selTicket = selected || active[0]?.ticket_id || cases[0]?.ticket_id || null;

  const renderRow = (c: AgentCaseListItem) => (
    <button
      key={c.id}
      onClick={() => setSelected(c.ticket_id)}
      className={cn(
        'w-full border-b border-slate-100 px-4 py-3 text-left transition-colors',
        selTicket === c.ticket_id ? 'bg-[#0F99FD]/5' : 'hover:bg-slate-50',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] text-slate-400">{c.ticket_number}</span>
        <StatusBadge status={c.status} />
      </div>
      <div className="mt-1 truncate text-sm font-medium text-slate-800">{c.subject}</div>
      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
        {c.internal_comm_count > 0 && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.internal_comm_count}</span>}
        <span>{fmt(c.updated_at)}</span>
      </div>
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <div>
          <h1 className="text-lg font-bold text-slate-800">{t('shell:nav.agentCases', 'Agent-Fälle')}</h1>
          <p className="text-sm text-slate-500">Was der Cloud-Agent bearbeitet — Verlauf, interne Kommunikationen, nächste Schritte.</p>
        </div>
        <button onClick={() => refetch()} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100">
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="w-[340px] shrink-0 overflow-y-auto border-r border-slate-100">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : cases.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">{t('common:x3.noCasesStartAgent', 'Noch keine Fälle. Starte den Cloud-Agent.')}</div>
          ) : (
            <>
              {active.length > 0 && <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{t('common:x3.activeCount', 'Aktiv (')}{active.length})</div>}
              {active.map(renderRow)}
              {doneList.length > 0 && <div className="px-4 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{t('views:f.sCompleted', 'Abgeschlossen')}</div>}
              {doneList.map(renderRow)}
            </>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {selTicket ? <CaseDetail ticketId={selTicket} /> : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">{t('common:x3.chooseCase', 'Wähle einen Fall')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentCasesView;
